import {
  CallToActionCatalogItem,
  CallToActionLanguageOption,
  DEFAULT_CTA_LANGUAGE,
  resolveCallToActionText,
} from '~/shared/const/callToAction';

export const splitToArray = (val?: string | string[]) =>
  typeof val === 'string'
    ? val
        .split(/\r?\n/)
        .map(str => str.trim())
        .filter(Boolean)
    : Array.isArray(val)
    ? val
    : [];

const isLocalHost = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const normalizeMediaPath = (value: unknown) => {
  if (typeof value !== 'string' || !isLocalHost()) return value;
  const sanitized = value
    .trim()
    .replace(/\\/g, '/')
    .replace(/^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?/i, '')
    .replace(/^\/+/, '')
    .replace(/^storage\/app\/public\//i, '')
    .replace(/^app\/public\//i, '')
    .replace(/^storage\//i, '');
  return sanitized;
};

const normalizeMediaList = (val: unknown) => {
  if (!Array.isArray(val)) return [];
  return val.map(item => normalizeMediaPath(item));
};

export const buildDemography = (formState: any) => ({
  genders: [
    formState.male,
    formState.female,
    formState.unspecified_gender,
  ].filter(Boolean),
  age: {
    from: formState.ageFrom,
    to: formState.ageTo,
    undefined: formState.unspecified_age,
  },
});

export const buildSchedule = (schedule: any[], daysOfWeekEN: any) =>
  schedule?.map(day => ({
    day: daysOfWeekEN[day.day],
    from: {hour: day.from.hour, quarter: (day.from.minute - 2) * 15},
    to: {hour: day.to.hour, quarter: (day.to.minute - 2) * 15},
  }));

// Final URL suffix хранится как чистая строка параметров без ведущих ? или &.
const normalizeFinalUrlSuffix = (value: unknown) =>
  String(value || '')
    .trim()
    .replace(/^[?&]+/, '');

export const buildTemplateData = (
  formState: any,
  uiState: any,
  extras: {
    id?: string;
    schedule?: any;
    daysOfWeekEN?: any;
    lang?: any;
    callToActionItems?: CallToActionCatalogItem[];
    callToActionLanguages?: CallToActionLanguageOption[];
  } = {},
) => {
  const budgetUsdFrom = Number(formState.budget_usd_from);
  const budgetUsdTo = Number(formState.budget_usd_to);
  const hasUsdBudgetRange =
    Number.isFinite(budgetUsdFrom) &&
    Number.isFinite(budgetUsdTo) &&
    budgetUsdFrom >= 1 &&
    budgetUsdTo >= budgetUsdFrom;

  const hasAdaptiveLayoutsChoice =
    typeof formState.asset_automation_adaptive_layouts === 'boolean';
  const hasGeneratedVideosChoice =
    typeof formState.asset_automation_generated_videos === 'boolean';
  const hasOptimizedTargetingChoice = typeof formState.optimized_targeting === 'boolean';

  const payload: any = {
    name: formState.name,
    geo: formState.geo || null,
    budget: hasUsdBudgetRange
      ? [budgetUsdFrom, budgetUsdTo]
      : splitToArray(formState.budget).map(v => Number(v)).filter(v => !Number.isNaN(v)),
    ...(hasUsdBudgetRange
      ? {
          budget_usd_from: budgetUsdFrom,
          budget_usd_to: budgetUsdTo,
          budget_base_currency: 'USD',
        }
      : {}),
    headlines: splitToArray(formState.headlines),
    descriptions: splitToArray(formState.descriptions),
    append_tag: formState.append_tag,
    // Поле тега всегда сохраняем отдельно: ручное значение должно работать
    // и при выключенной автоматической подстановке.
    custom_utm: String(formState.custom_utm || '').trim(),
    final_url_suffix: normalizeFinalUrlSuffix(formState.final_url_suffix),
    // Новая модель повторяет Google Ads: каждая automation-настройка хранится отдельно.
    ...(hasAdaptiveLayoutsChoice
      ? {asset_automation_adaptive_layouts: formState.asset_automation_adaptive_layouts}
      : {}),
    ...(hasGeneratedVideosChoice
      ? {asset_automation_generated_videos: formState.asset_automation_generated_videos}
      : {}),
    // Для старых потребителей шаблонов сохраняем legacy-ключ только когда обе настройки
    // совпадают. Если выбор разный, legacy-значение было бы неоднозначным.
    ...(hasOptimizedTargetingChoice
      ? {optimized_targeting: formState.optimized_targeting}
      : {}),
    placements: Object.values(formState.placements || {}).some(Boolean)
      ? formState.placements
      : undefined,
    demography: buildDemography(formState),
    languages: extras.lang.length ? extras.lang : [],
    devices: formState.devices?.length ? formState.devices : [],
    schedule: extras.schedule.length
      ? buildSchedule(extras.schedule, extras.daysOfWeekEN)
      : [],
    logo: formState.onNameChangeLogo?.length
      ? normalizeMediaList(formState.onNameChangeLogo)
      : formState.logo?.length
      ? normalizeMediaList(formState.logo)
      : [],
    ...(formState.call_to_action_key
      ? {
          call_to_action_key: formState.call_to_action_key,
          call_to_action_language: formState.call_to_action_language || DEFAULT_CTA_LANGUAGE,
          // В шаблоне дополнительно храним и итоговый текст, чтобы backend и история
          // оставались совместимыми со старыми сценариями чтения.
          call_to_action: resolveCallToActionText(
            formState.call_to_action_key,
            formState.call_to_action_language || DEFAULT_CTA_LANGUAGE,
            extras.callToActionItems,
            extras.callToActionLanguages,
          ),
        }
      : {}),
    ...(!uiState.visible
      ? (() => {
          const sourceImages = formState.source_image || [];
          const selectedFormats = Array.isArray(formState.marketing_image_formats)
            ? formState.marketing_image_formats
            : [];
          return {
            source_image: normalizeMediaList(sourceImages),
            marketing_image_formats: selectedFormats,
          };
        })()
      : {}),
    ...(uiState.visible
      ? {marketing_link: splitToArray(formState.marketing_link)}
      : {}),
  };

  if (extras.id) payload.id = extras.id;

  return payload;
};
